import clsx from 'clsx';
import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HairToneModelOptions, buildHairToneOptions, writeUserToStorage } from '@vestico/model';

import { FormValidationContext } from '../../CommonAppContext';
import { ColorSelect } from '../../components/beauty/ColorSelect';
import { useIsMounted } from '../../hooks/mounted';
import { StepProps } from '../../model/step';
import { UserContext } from '../../model/userContext';
import StepContainer from '../StepContainer';
import style from './HairToneForm.module.css';

export const HairToneForm = (props: StepProps) => {
  const { user } = useContext(UserContext);
  const formValidationContext = useContext(FormValidationContext);
  const mounted = useIsMounted();
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation(['shared', 'model']);

  useMemo(() => {
    const isSubmittingFromExternally = props.submitting;
    if (isSubmittingFromExternally) {
      setSubmitting(isSubmittingFromExternally);
    }
    return isSubmittingFromExternally;
  }, [props.submitting]);

  const options = HairToneModelOptions;
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(user?.hairTone ? options.indexOf(user.hairTone) : undefined);
  const error = useMemo(() => !!(mounted.current && selectedOptionIndex === undefined), [selectedOptionIndex, submitting, mounted]);
  const formValid = useMemo(() => !error, [error]);
  const [showErrorIfNeeded, setShowErrorIfNeeded] = useState(false);

  const didUpdateConfig = useCallback((newIndex: number) => {
    setSelectedOptionIndex(newIndex);
    setSubmitting(true);
  }, []);

  useMemo(() => {
    if (!user) {
      return;
    }
    if (!user.hairTone) {
      return;
    }
    setSelectedOptionIndex(options.indexOf(user.hairTone));
  }, [options, user]);

  const updateUserProperties = useCallback(() => {
    if (!user) {
      console.error('no user fetched');
      return;
    }
    if (selectedOptionIndex === undefined) {
      return;
    }
    user.hairTone = options[selectedOptionIndex];
  }, [options, selectedOptionIndex, user]);

  useEffect(() => {
    if (!submitting) {
      return;
    }

    formValidationContext.updateStep(props.path, formValid);

    setShowErrorIfNeeded(true);

    if (!formValid) {
      setSubmitting(false);
      return;
    }

    if (!user) {
      console.error('missing user');
      return;
    }

    // store data
    updateUserProperties();
    writeUserToStorage(user);
    setShowErrorIfNeeded(false);
    formValidationContext.submit();
  }, [formValid, formValidationContext, props.path, submitting, updateUserProperties, user]);

  const displayOptions = useMemo(() => buildHairToneOptions(t), [t]);
  const onFormSubmit = useCallback((e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
  }, []);

  return (
    <StepContainer title={props.title}>
      <form
        className={clsx(style.formLayout)}
        onSubmit={onFormSubmit}
        // skipcq JS-0455
        // eslint-disable-next-line react/no-unknown-property
        body-scroll-lock-ignore="true"
      >
        <ColorSelect
          options={displayOptions}
          selectedOptionIndex={selectedOptionIndex}
          didUpdateConfig={didUpdateConfig}
          errorText={t('LOC_ENTER_INFO_STEP_ERROR_HAIR_TONE')}
          showErrorIfNeeded={showErrorIfNeeded}
        />
      </form>
    </StepContainer>
  );
};

export default HairToneForm;
